import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Plus, Trash2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPrice, formatDuration } from "@/lib/format";
import { ServiceFields, EMPTY_SERVICE, toServicePayload } from "@/components/ServiceDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function AccountStep() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register({ ...form, role: "provider" }); toast.success("Account created"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="lyb-account-form">
      <h2 className="font-heading text-xl font-semibold sm:col-span-2">Create your provider account</h2>
      <div><Label>Your name</Label><Input data-testid="lyb-name" required value={form.name} onChange={set("name")} className="mt-1.5 h-12" /></div>
      <div><Label>Phone</Label><Input data-testid="lyb-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-12" /></div>
      <div><Label>Email</Label><Input data-testid="lyb-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-12" /></div>
      <div><Label>Password</Label><Input data-testid="lyb-password" type="password" minLength={8} required value={form.password} onChange={set("password")} className="mt-1.5 h-12" /></div>
      <button data-testid="lyb-account-submit" disabled={busy} className="flex h-12 items-center justify-center rounded-xl bg-sky-600 font-semibold text-white sm:col-span-2">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account & continue"}</button>
      <p className="text-sm text-slate-500 sm:col-span-2">Already registered? <Link to="/sign-in?next=/list-your-business" className="font-semibold text-sky-700" data-testid="lyb-sign-in">Sign in</Link></p>
    </form>
  );
}

export function Submitted() {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid="lyb-submitted">
      <CheckCircle2 className="mx-auto h-14 w-14 text-teal-600" />
      <h2 className="mt-4 font-heading text-2xl font-bold text-slate-900">Your business profile has been submitted for review.</h2>
      <p className="mx-auto mt-2 max-w-md text-slate-600">Our team will review your application. Your business will appear publicly once approved. Meanwhile, you can fine-tune services and availability.</p>
      <Link to="/provider-dashboard" data-testid="lyb-go-dashboard" className="mt-6 inline-flex h-12 items-center rounded-xl bg-sky-600 px-6 font-semibold text-white">Go to Provider Dashboard</Link>
    </motion.div>
  );
}

export function ServicesStep({ services, setServices }) {
  const [draft, setDraft] = useState(EMPTY_SERVICE);
  const add = () => {
    if (draft.name.trim().length < 2 || draft.price === "" || Number(draft.duration_minutes) < 15) return toast.error("Enter a service name, price and duration (min 15 min)");
    setServices([...services, { ...toServicePayload(draft), _key: `${Date.now()}-${Math.random()}` }]);
    setDraft(EMPTY_SERVICE);
  };
  return (
    <div className="space-y-6">
      {services.map((s, i) => (
        <div key={s._key} className="flex items-center justify-between rounded-xl border border-slate-200 p-4" data-testid={`lyb-service-${i}`}>
          <div><p className="font-semibold">{s.name}</p><p className="flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" />{formatDuration(s.duration_minutes)} · {s.service_type}</p></div>
          <div className="flex items-center gap-3"><span className="font-heading font-bold">{formatPrice(s.price)}</span>
            <button type="button" data-testid={`lyb-remove-service-${i}`} onClick={() => setServices(services.filter((x) => x._key !== s._key))} className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button></div>
        </div>
      ))}
      <div className="rounded-2xl border border-dashed border-slate-300 p-5">
        <ServiceFields value={draft} onChange={setDraft} idPrefix="lyb-service" />
        <button type="button" data-testid="lyb-add-service" onClick={add} className="mt-4 inline-flex h-11 items-center gap-2 rounded-xl border border-sky-600 px-4 text-sm font-semibold text-sky-700 hover:bg-sky-50"><Plus className="h-4 w-4" />Add service</button>
      </div>
    </div>
  );
}
